// Customer Portal uses an old 1.x version of ds-components library
// In latest 2.x version (https://www.npmjs.com/package/@ds/react-components), Table component has been removed from the library somehow
// This Table component from SS New with some changes to make the onRowClick event work properly
import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import MaterialTable from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import TableHead from './TableHead.js';
import Pagination from './TablePagination.js';
import TableExpandableRow from './TableExpandableRow.js';

const styles = (theme) => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(3),
    padding: '0 15px',
  },
  noBackground: {
    background: 'transparent',
    boxShadow: 'none',
    padding: 0,
    margin: 0,
  },
  table: {
    '& tr:last-child td': {
      borderBottom: 'none',
    },
  },
  tableWrapper: {
    // overflowX: 'auto',
  },
  cell: {
    padding: '18px 15px',
  },
  cellMobile: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  row: {},
  noResults: {
    textAlign: 'center',
    fontSize: '22px',
    color: '#6b6c6f',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    height: 60,
  },
  pagination: {
    marginLeft: 'auto',
  },
  noPadding: {
    padding: 0,
  },
});

function desc(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
  return order === 'desc'
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy);
}

class Table extends React.PureComponent {
  state = {
    orderBy: this.props.orderBy,
    order: this.props.order,
    rowsPerPage: this.props.rowsPerPage || 10,
  };

  handleRequestSort = (event, property) => {
    const orderBy = property;
    let order = 'desc';

    if (this.state.orderBy === property && this.state.order === 'desc') {
      order = 'asc';
    }
    this.setState({ order, orderBy });
  };

  handleClick = (event, row) => {
    if (this.props.onRowClick) {
      this.props.onRowClick(event, row);
    }
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value });
  };

  componentDidMount = () => {
    this.setState({ orderBy: this.props.orderBy });
  };

  render() {
    const {
      classes,
      data,
      metadata,
      showNoResults,
      footerText,
      enableSorting,
      showFooter,
      noBackground,
      showEmptyRows,
      currentPage,
      onPageChange,
      enableExpandableRow,
      expandableColumn,
    } = this.props;
    const page = currentPage || 0;
    const { order, orderBy, rowsPerPage } = this.state;
    const emptyRows = showEmptyRows
      ? rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage)
      : 0;
    const defaultColWidth = (1 / metadata.length) * 100 + '%';
    const pageCount = Math.ceil(data.length / rowsPerPage);
    const showHeader = !!metadata.find((row) => !!row.label);
    return (
      <div>
        <Paper
          className={classNames(classes.root, {
            [classes.noBackground]: noBackground,
          })}>
          <div className={classes.tableWrapper}>
            <MaterialTable
              className={classes.table}
              aria-labelledby="tableTitle">
              <colgroup>
                {metadata.map((r) => {
                  return <col key={r.id} width={r.width || defaultColWidth} />;
                })}
              </colgroup>
              {showHeader && (
                <TableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={this.handleRequestSort}
                  rowCount={data.length}
                  rows={metadata}
                  enableSorting={enableSorting}
                />
              )}
              {showNoResults ? (
                <TableBody>
                  <TableRow style={{ height: 49 }}>
                    <TableCell
                      colSpan={metadata.length}
                      className={classNames(classes.cell, classes.noResults)}>
                      No Results Found
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {stableSort(data, getSorting(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, i) => {
                      if (
                        enableExpandableRow &&
                        row.__children &&
                        row.__children.length > 0
                      ) {
                        return (
                          <TableExpandableRow
                            metadata={metadata}
                            row={row}
                            expandableColumn={expandableColumn}
                            hover
                            onClick={(event) => this.handleClick(event, row.id)}
                            tabIndex={-1}
                            key={i}
                            className={classes.row}
                          />
                        );
                      }
                      return (
                        <TableRow
                          hover
                          onClick={(event) => this.handleClick(event, row)}
                          tabIndex={-1}
                          key={i}
                          className={classes.row}>
                          {metadata.map((column, j) => (
                            <TableCell
                              key={j}
                              align={column.alignment}
                              className={classNames(
                                classes.cell,
                                column.className,
                                { [classes.cellMobile]: column.hideInMobile },
                                {
                                  [classes.noPadding]:
                                    column.disableCellPadding,
                                }
                              )}>
                              {row[column.renderingField]}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 49 * emptyRows }}>
                      <TableCell
                        colSpan={metadata.length}
                        className={classes.cell}
                      />
                    </TableRow>
                  )}
                </TableBody>
              )}
            </MaterialTable>
          </div>
        </Paper>

        {showFooter && (
          <div className={classes.footer}>
            <div>{footerText}</div>
            {pageCount > 1 && (
              <Pagination
                className={classes.pagination}
                dataCount={data.length}
                onPageChange={onPageChange}
                currentPage={page}
                rowsPerPage={rowsPerPage}
                pageCount={pageCount}
              />
            )}
          </div>
        )}
      </div>
    );
  }
}

Table.propTypes = {
  classes: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  metadata: PropTypes.array.isRequired,
  onRowClick: PropTypes.func,
  showNoResults: PropTypes.bool,
  rowsPerPage: PropTypes.number,
  footerText: PropTypes.object,
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
  enableSorting: PropTypes.bool,
  showFooter: PropTypes.bool,
  noBackground: PropTypes.bool,
  showEmptyRows: PropTypes.bool,
  enableExpandableRow: PropTypes.bool,
  expandableColumn: PropTypes.string,
};

export default withStyles(styles)(Table);
